import React, { Fragment, useContext, useEffect, useState } from "react";
import useBreadcrumbs from "../../../utils/hooks/useBreadcrumbs";
import { MdPerson } from "react-icons/md";
import Alert from "../../../UI/Alert/Alert";
import { NotifyContext } from "../../../context/notifyContext";
import { useNavigate, useParams } from "react-router-dom";
import AdminAPI from "../../../services/api/AdminService";
import { AdminEntity } from "../../../Types/admins";
import ButtonLoading from "../../../UI/Button/ButtonLoading";
import UserAPI from "../../../services/api/UserService";

export default function AdminDetails() {
  const { id } = useParams();
  const notify = useContext(NotifyContext);
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [admin, setAdmin] = useState<AdminEntity | null>(null);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [blocking, setBlocking] = useState(false);

  useBreadcrumbs([
    {
      label: "Admins",
      icon: <MdPerson />,
      is_link: true,
      href: "/app/admins",
    },
    {
      label: "Détails",
      is_link: false,
    },
  ]);

  useEffect(() => {
    if (!id) return;
    AdminAPI.get_admin(Number(id))
      .then((response: any) => response?.data)
      .then((data: any) => {
        const a = data?.admin as AdminEntity;
        setAdmin(a || null);
        setUserPermissions(a?.permissions || []);
        setError("");
      })
      .catch((error) => {
        setError(error);
        notify(error, "error");
      });
  }, [id, notify]);

  if (error) return <Alert color="error">{error}</Alert>;

  if (!admin) return <div className="p-4">Chargement...</div>;

  const permissions = [
    {
      tag: "Blog",
      permissions: [{ title: "Comment a blog", code: "create_blog_comments" }],
    },
    {
      tag: "Community",
      permissions: [
        { title: "View community requests", code: "view_community_requests" },
        { title: "Create community requests", code: "create_community_requests" },
        { title: "Edit community requests", code: "edit_community_requests" },
        { title: "Delete community requests", code: "delete_community_requests" },
        { title: "Comment a community", code: "create_forum_comments" },
      ],
    },
    {
      tag: "Users",
      permissions: [
        { title: "View student profiles", code: "view_student_profiles" },
        { title: "View tutor profiles", code: "view_tutor_profiles" },
        { title: "Edit tutor profiles", code: "edit_tutor_profiles" },
      ],
    },
    {
      tag: "Admins",
      permissions: [
        { title: "Manage all permissions", code: "manage_all_permissions" },
        { title: "Manage users", code: "manage_users" },
      ],
    },
  ];

  const handleCheckboxChange = (code: string) => {
    const updatedPermissions = [...userPermissions];
    const idx = updatedPermissions.findIndex((p) => p === code);
    if (idx > -1) updatedPermissions.splice(idx, 1);
    else updatedPermissions.push(code);
    setUserPermissions(updatedPermissions);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!admin) return;
    setLoadingPermissions(true);
    UserAPI.update_user_permissions(admin.user_id, userPermissions)
      .then(() => {
        notify("Mise à jour des permissions effectuée.");
      })
      .catch((err) => notify(err, "error"))
      .finally(() => setLoadingPermissions(false));
  };

  return (
    <Fragment>
      <div className="container mx-auto my-5 p-5">
        <div className="md:flex no-wrap md:-mx-2 ">
          <div className="w-full md:w-3/12 md:mx-2">
            <div className="bg-white p-3 border-t-4 border-green-400">
              <div className="image overflow-hidden">
                <img
                  className="h-auto w-full mx-auto"
                  src={
                    admin.avatar_url ||
                    "https://lavinephotography.com.au/wp-content/uploads/2017/01/PROFILE-Photography-112.jpg"
                  }
                  alt=""
                />
              </div>
              <h1 className="text-gray-900 font-bold text-xl leading-8 my-1">
                {admin.first_name} {admin.last_name}
              </h1>
              <h3 className="text-gray-600 font-lg text-semibold leading-6">
                {admin.is_super_admin ? <span className="text-xs text-orange-600 font-bold border border-orange-600 rounded px-1">Super Admin</span> : "Admin"}
              </h3>
              <ul className="bg-gray-100 text-gray-600 hover:text-gray-700 hover:shadow py-2 px-3 mt-5 divide-y rounded shadow-sm">
                <li className="flex items-center py-3">
                  <span>Email</span>
                  <span className="ml-auto">
                    <a className="text-blue-800" href={`mailto:${admin.email}`}>
                      {admin.email}
                    </a>
                  </span>
                </li>
                <li className="flex items-center py-3">
                  <span>Statut</span>
                  <span className="ml-auto">
                    {admin.user_status === "PENDING" && (
                      <span className="bg-gray-500 py-1 px-2 rounded text-white text-sm">
                        En attente
                      </span>
                    )}

                    {admin.user_status === "VALIDATED" && (
                      <span className="bg-green-500 py-1 px-2 rounded text-white text-sm">
                        Validé
                      </span>
                    )}

                    {admin.user_status === "BLOCKED" && (
                      <span className="bg-red-500 py-1 px-2 rounded text-white text-sm">
                        Bloqué
                      </span>
                    )}

                    {admin.user_status === "REFUSED" && (
                      <span className="bg-yellow-500 py-1 px-2 rounded text-white text-sm">
                        Refusé
                      </span>
                    )}

                    {admin.user_status === "CONFIRMED" && (
                      <span className="bg-gray-500 py-1 px-2 rounded text-white text-sm">
                        En attente
                      </span>
                    )}
                  </span>
                </li>
                <li className="flex items-center py-3">
                  <span>Ajouté le</span>
                  <span className="ml-auto">{new Date(admin.created_at).toDateString()}</span>
                </li>
                <li className="flex items-center py-3 gap-2">
                  <button
                    className="bg-red-600 text-white text-sm font-semibold rounded-lg hover:opacity-80 p-2"
                    onClick={() => {
                      if (!id) return;
                      if (!window.confirm("Supprimer cet admin ?")) return;
                      AdminAPI.delete_admin(Number(id))
                        .then(() => {
                          notify("Admin supprimé", "success");
                          navigate("/app/admins");
                        })
                        .catch((err) => notify(err, "error"));
                    }}
                  >
                    Supprimer
                  </button>
                  <button
                    className={`text-white text-sm font-semibold rounded-lg hover:opacity-80 p-2 ${admin.user_status === "BLOCKED" ? "bg-green-600" : "bg-yellow-600"}`}
                    onClick={() => {
                      if (!id) return;
                      setBlocking(true);
                      const shouldBlock = admin.user_status !== "BLOCKED";
                      AdminAPI.block_admin(Number(id), shouldBlock)
                        .then(() => {
                          notify(shouldBlock ? "Admin bloqué" : "Admin débloqué", "success");
                          // Refresh admin data to update status
                          AdminAPI.get_admin(Number(id))
                            .then((response: any) => response?.data)
                            .then((data: any) => {
                              const a = data?.admin as AdminEntity;
                              setAdmin(a || null);
                              setUserPermissions(a?.permissions || []);
                            })
                            .catch((error) => {
                              console.error("Error refreshing admin data:", error);
                            });
                        })
                        .catch((err) => notify(err, "error"))
                        .finally(() => setBlocking(false));
                    }}
                    disabled={blocking}
                  >
                    {blocking ? "..." : admin.user_status === "BLOCKED" ? "Débloquer" : "Bloquer"}
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="w-full md:w-9/12 mx-2 h-64">
            <div className="bg-white p-3 shadow-sm rounded-sm">
              <div className="flex items-center space-x-2 font-semibold text-gray-900 leading-8">
                <span className="text-green-500">
                  <svg
                    className="h-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </span>
                <span className="tracking-wide">Permissions de l'admin</span>
              </div>
              <div className="text-gray-700">
                <form className="mt-4 mb-8" onSubmit={handleSubmit}>
                  {permissions.map((group) => (
                    <div key={group.tag} className="mb-4">
                      <h2 className="text-base font-semibold mb-2">{group.tag}</h2>
                      <div className="grid grid-cols-3 gap-4">
                        {group.permissions.map((permission) => (
                          <label key={permission.code} className="flex items-center text-sm">
                            <input
                              type="checkbox"
                              className="form-checkbox mr-2"
                              checked={userPermissions.some((p) => p === permission.code)}
                              onChange={() => handleCheckboxChange(permission.code)}
                            />
                            {permission.title}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                  <ButtonLoading
                    type="submit"
                    loading={loadingPermissions}
                    label="Save permissions"
                    className="mt-4"
                  />
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}


